/**
 * Display labels for bond reference data enums.
 *
 * This file contains mappings from enum values to user-friendly display names
 * for bond reference data types like currencies, countries, etc.
 *
 * @example
 * ```tsx
 * import { CURRENCY_LABELS } from '@/shared/constants/reference-data-labels'
 * import { Currency } from '@moment/trading-types/reference-data'
 *
 * const displayName = CURRENCY_LABELS[Currency.USD]  // 'United States dollar'
 * ```
 */

import { Currency } from '@moment/trading-types/reference-data'

/**
 * Display names for ISO 4217 currency codes.
 *
 * Maps {@link Currency} enum values to human-readable currency names.
 * Includes both active and historical/deprecated currency codes.
 */
export const CURRENCY_LABELS: Record<Currency, string> = {
  [Currency.ADF]: 'Andorran franc',
  [Currency.ADP]: 'Andorran peseta',
  [Currency.AED]: 'United Arab Emirates dirham',
  [Currency.AFA]: 'Afghan afghani',
  [Currency.AFN]: 'Afghan afghani',
  [Currency.ALL]: 'Albanian lek',
  [Currency.AMD]: 'Armenian dram',
  [Currency.ANG]: 'Netherlands Antillean guilder',
  [Currency.AOA]: 'Angolan kwanza',
  [Currency.AON]: 'Angolan new kwanza',
  [Currency.AOR]: 'Angolan kwanza reajustado',
  [Currency.ARA]: 'Argentine austral',
  [Currency.ARL]: 'Argentine peso ley',
  [Currency.ARP]: 'Argentine peso argentino',
  [Currency.ARS]: 'Argentine peso',
  [Currency.ATS]: 'Austrian schilling',
  [Currency.AUD]: 'Australian dollar',
  [Currency.AWG]: 'Aruban florin',
  [Currency.AZM]: 'Azerbaijani manat',
  [Currency.AZN]: 'Azerbaijani manat',
  [Currency.BAM]: 'Bosnia and Herzegovina convertible mark',
  [Currency.BBD]: 'Barbados dollar',
  [Currency.BCF]: 'Currency not specified',
  [Currency.BDT]: 'Bangladeshi taka',
  [Currency.BEF]: 'Belgian franc',
  [Currency.BGL]: 'Bulgarian lev',
  [Currency.BGN]: 'Bulgarian lev',
  [Currency.BHD]: 'Bahraini dinar',
  [Currency.BIF]: 'Burundian franc',
  [Currency.BMD]: 'Bermudian dollar',
  [Currency.BND]: 'Brunei dollar',
  [Currency.BOB]: 'Boliviano',
  [Currency.BOP]: 'Bolivian peso',
  [Currency.BOV]: 'Bolivian Mvdol (funds code)',
  [Currency.BRB]: 'Brazilian cruzeiro',
  [Currency.BRC]: 'Brazilian cruzado',
  [Currency.BRE]: 'Brazilian cruzeiro',
  [Currency.BRL]: 'Brazilian real',
  [Currency.BRN]: 'Brazilian cruzado novo',
  [Currency.BRR]: 'Brazilian cruzeiro real',
  [Currency.BSD]: 'Bahamian dollar',
  [Currency.BTN]: 'Bhutanese ngultrum',
  [Currency.BWP]: 'Botswana pula',
  [Currency.BWT]: 'Botswana tebe',
  [Currency.BYR]: 'Belarusian ruble',
  [Currency.BZD]: 'Belize dollar',
  [Currency.CAD]: 'Canadian dollar',
  [Currency.CAX]: 'Canadian dollar (next day)',
  [Currency.CDF]: 'Congolese franc',
  [Currency.CHE]: 'WIR Bank',
  [Currency.CHF]: 'Swiss franc',
  [Currency.CHW]: 'WIR Bank',
  [Currency.CLE]: 'Chilean escudo',
  [Currency.CLF]: 'Unidad de Fomento (funds code)',
  [Currency.CLP]: 'Chilean peso',
  [Currency.CNH]: 'Chinese yuan (offshore)',
  [Currency.CNY]: 'Chinese yuan',
  [Currency.COP]: 'Colombian peso',
  [Currency.COU]: 'Columbian Unidad de Valor Real',
  [Currency.CRC]: 'Costa Rican colon',
  [Currency.CSD]: 'Serbian dinar',
  [Currency.CSK]: 'Czechoslovak koruna',
  [Currency.CUC]: 'Cuban convertible peso',
  [Currency.CUP]: 'Cuban peso',
  [Currency.CVE]: 'Cape Verde escudo',
  [Currency.CYP]: 'Cypriot pound',
  [Currency.CZK]: 'Czech koruna',
  [Currency.DDM]: 'East German mark',
  [Currency.DEM]: 'German mark',
  [Currency.DJF]: 'Djiboutian franc',
  [Currency.DKK]: 'Danish krone',
  [Currency.DOP]: 'Dominican peso',
  [Currency.DZD]: 'Algerian dinar',
  [Currency.ECS]: 'Ecuadorian sucre',
  [Currency.ECV]: 'Ecuador Unidad de Valor Constante',
  [Currency.EEK]: 'Estonian kroon',
  [Currency.EGP]: 'Egyptian pound',
  [Currency.ERN]: 'Eritrean nakfa',
  [Currency.ESA]: 'Spanish peseta',
  [Currency.ESB]: 'Spanish peseta',
  [Currency.ESP]: 'Spanish peseta',
  [Currency.ETB]: 'Ethiopian birr',
  [Currency.EUR]: 'Euro',
  [Currency.EUX]: 'Euro (next day)',
  [Currency.FIM]: 'Finnish markka',
  [Currency.FJD]: 'Fiji dollar',
  [Currency.FKP]: 'Falkland Islands pound',
  [Currency.FRF]: 'French franc',
  [Currency.GBP]: 'Pound sterling',
  [Currency.GBX]: 'Penny sterling',
  [Currency.GEL]: 'Georgian lari',
  [Currency.GHC]: 'Ghanaian cedi',
  [Currency.GHS]: 'Ghanaian cedi',
  [Currency.GIP]: 'Gibraltar pound',
  [Currency.GMD]: 'Gambian dalasi',
  [Currency.GNE]: 'Guinean syli',
  [Currency.GNF]: 'Guinean franc',
  [Currency.GQE]: 'Equatorial Guinean ekwele',
  [Currency.GRD]: 'Greek drachma',
  [Currency.GTQ]: 'Guatemalan quetzal',
  [Currency.GWP]: 'Guinea-Bissau peso',
  [Currency.GYD]: 'Guyanese dollar',
  [Currency.HKD]: 'Hong Kong dollar',
  [Currency.HNL]: 'Honduran lempira',
  [Currency.HRK]: 'Croatian kuna',
  [Currency.HTG]: 'Haitian gourde',
  [Currency.HUF]: 'Hungarian forint',
  [Currency.IDR]: 'Indonesian rupiah',
  [Currency.IEP]: 'Irish pound',
  [Currency.ILA]: 'Israeli agorot',
  [Currency.ILP]: 'Israeli lira',
  [Currency.ILR]: 'Israeli shekel',
  [Currency.ILS]: 'Israeli new sheqel',
  [Currency.ISJ]: 'Icelandic old krona',
  [Currency.INR]: 'Indian rupee',
  [Currency.IQD]: 'Iraqi dinar',
  [Currency.IRR]: 'Iranian rial',
  [Currency.ISK]: 'Icelandic krona',
  [Currency.ITL]: 'Italian lira',
  [Currency.JMD]: 'Jamaican dollar',
  [Currency.JOD]: 'Jordanian dinar',
  [Currency.JPX]: 'Japanese yen (next day)',
  [Currency.JPY]: 'Japanese yen',
  [Currency.KES]: 'Kenyan shilling',
  [Currency.KGS]: 'Kyrgyzstani som',
  [Currency.KHR]: 'Cambodian riel',
  [Currency.KMF]: 'Comoro franc',
  [Currency.KPW]: 'North Korean won',
  [Currency.KRW]: 'South Korean won',
  [Currency.KWD]: 'Kuwaiti dinar',
  [Currency.KWF]: 'Kuwaiti fils',
  [Currency.KYD]: 'Cayman Islands dollar',
  [Currency.KZT]: 'Kazakhstani tenge',
  [Currency.LAJ]: 'Lao kip',
  [Currency.LAK]: 'Lao kip',
  [Currency.LBP]: 'Lebanese pound',
  [Currency.LKR]: 'Sri Lanka rupee',
  [Currency.LRD]: 'Liberian dollar',
  [Currency.LSL]: 'Lesotho loti',
  [Currency.LTL]: 'Lithuanian litas',
  [Currency.LUF]: 'Luxembourg franc',
  [Currency.LVL]: 'Latvian lats',
  [Currency.LYD]: 'Libyan dinar',
  [Currency.MAD]: 'Moroccan dirham',
  [Currency.MAF]: 'Moroccan franc',
  [Currency.MCF]: 'Monegasque franc',
  [Currency.MDL]: 'Moldovan leu',
  [Currency.MGA]: 'Malagasy ariary',
  [Currency.MGF]: 'Malagasy franc',
  [Currency.MKD]: 'Macedonian denar',
  [Currency.MKN]: 'Macedonian denar',
  [Currency.MLF]: 'Malian franc',
  [Currency.MMK]: 'Myanmar kyat',
  [Currency.MNT]: 'Mongolian tugrik',
  [Currency.MOP]: 'Macanese pataca',
  [Currency.MRO]: 'Mauritanian ouguiya',
  [Currency.MTL]: 'Maltese lira',
  [Currency.MUR]: 'Mauritian rupee',
  [Currency.MVQ]: 'Maldivian rupee',
  [Currency.MVR]: 'Maldivian rufiyaa',
  [Currency.MWK]: 'Malawian kwacha',
  [Currency.MXN]: 'Mexican peso',
  [Currency.MXP]: 'Mexican peso',
  [Currency.MXV]: 'Mexican Unidad de Inversion (UDI)',
  [Currency.MYR]: 'Malaysian ringgit',
  [Currency.MZM]: 'Mozambican metical',
  [Currency.MZN]: 'Mozambican metical',
  [Currency.NAC]: 'Namibian cents',
  [Currency.NAD]: 'Namibian dollar',
  [Currency.NFD]: 'Newfoundland dollar',
  [Currency.NGN]: 'Nigerian naira',
  [Currency.NIO]: 'Cordoba oro',
  [Currency.NLG]: 'Dutch guilder',
  [Currency.NOK]: 'Norwegian krone',
  [Currency.NPR]: 'Nepalese rupee',
  [Currency.NZD]: 'New Zealand dollar',
  [Currency.OMR]: 'Omani rial',
  [Currency.PAB]: 'Panamanian balboa',
  [Currency.PEH]: 'Peruvian sol',
  [Currency.PEI]: 'Peruvian inti',
  [Currency.PEN]: 'Peruvian nuevo sol',
  [Currency.PGK]: 'Papua New Guinean kina',
  [Currency.PHP]: 'Philippine peso',
  [Currency.PKR]: 'Pakistani rupee',
  [Currency.PLN]: 'Polish zloty',
  [Currency.PLZ]: 'Polish zloty',
  [Currency.PTE]: 'Portuguese escudo',
  [Currency.PYG]: 'Paraguayan guarani',
  [Currency.QAR]: 'Qatari rial',
  [Currency.QZZ]: 'Testing currency code',
  [Currency.ROL]: 'Romanian leu',
  [Currency.RON]: 'Romanian new leu',
  [Currency.RSD]: 'Serbian dinar',
  [Currency.RUB]: 'Russian rouble',
  [Currency.RUR]: 'Russian ruble',
  [Currency.RWF]: 'Rwandan franc',
  [Currency.SAR]: 'Saudi riyal',
  [Currency.SBD]: 'Solomon Islands dollar',
  [Currency.SCR]: 'Seychelles rupee',
  [Currency.SDD]: 'Sudanese dinar',
  [Currency.SDG]: 'Sudanese pound',
  [Currency.SDP]: 'Sudanese pound',
  [Currency.SEK]: 'Swedish krona/kronor',
  [Currency.SGD]: 'Singapore dollar',
  [Currency.SHP]: 'Saint Helena pound',
  [Currency.SIT]: 'Slovenian tolar',
  [Currency.SKK]: 'Slovak koruna',
  [Currency.SLL]: 'Sierra Leonean leone',
  [Currency.SML]: 'San Marinese lira (currency union with ITL and VAL)',
  [Currency.SOS]: 'Somali shilling',
  [Currency.SRD]: 'Surinamese dollar',
  [Currency.SRG]: 'Surinamese guilder',
  [Currency.SSP]: 'South Sudanese Pound',
  [Currency.STD]: 'Sao Tome & Principe Dobra',
  [Currency.SUR]: 'Soviet rouble',
  [Currency.SVC]: 'Salvadoran colon',
  [Currency.SYP]: 'Syrian pound',
  [Currency.SZL]: 'Swaziland Lilangeni',
  [Currency.THB]: 'Thai baht',
  [Currency.TJR]: 'Tajikistani ruble',
  [Currency.TJS]: 'Tajikistani somoni',
  [Currency.TMM]: 'Turkmenistan manat',
  [Currency.TMT]: 'Turkmenistani manat',
  [Currency.TND]: 'Tunisian dinar',
  [Currency.TOP]: "Tonga Pa'anga",
  [Currency.TRL]: 'Turkish lira',
  [Currency.TRX]: "Turkish lira (000's)",
  [Currency.TRY]: 'Turkish lira',
  [Currency.TTD]: 'Trinidad and Tobago dollar',
  [Currency.TWD]: 'New Taiwan dollar',
  [Currency.TZS]: 'Tanzanian shilling',
  [Currency.UAH]: 'Ukrainian hryvnia',
  [Currency.UAK]: 'Ukrainian karbovanets',
  [Currency.UGS]: 'Ugandan shilling',
  [Currency.UGX]: 'Ugandan shilling',
  [Currency.USD]: 'United States dollar',
  [Currency.USN]: 'United States dollar (next day)',
  [Currency.USS]: 'United States dollar (same day)',
  [Currency.USX]: 'Cent',
  [Currency.UYI]: 'Uruguay Peso en Unidades Indexadas (URUIURUI)',
  [Currency.UYN]: 'Uruguayan old peso',
  [Currency.UYU]: 'Uruguayan peso',
  [Currency.UYW]: 'Uruguayan Unidad Previsional',
  [Currency.UZS]: 'Uzbekistan som',
  [Currency.VAL]: 'Vatican lira',
  [Currency.VEB]: 'Venezuelan bolivar',
  [Currency.VEF]: 'Venezuela Bolivar',
  [Currency.VES]: 'Venezuela Sovereign Bolivar',
  [Currency.VND]: 'Vietnam Dong',
  [Currency.VUV]: 'Vanuatu vatu',
  [Currency.WCU]: 'World currency unit',
  [Currency.WST]: 'Samoan tala',
  [Currency.XAF]: 'CFA franc BEAC',
  [Currency.XAG]: 'Silver (one troy ounce)',
  [Currency.XAU]: 'Gold (one troy ounce)',
  [Currency.XBA]: 'European Composite Unit (EURCO)',
  [Currency.XBB]: 'European Monetary Unit (E.M.U.-6)',
  [Currency.XBC]: 'European Unit of Account 9 (E.U.A.-9)',
  [Currency.XBD]: 'European Unit of Account 17 (E.U.A.-17)',
  [Currency.XBT]: 'Bitcoin',
  [Currency.XCD]: 'East Caribbean dollar',
  [Currency.XCU]: 'Currency not specified',
  [Currency.XDR]: 'Special Drawing Rights',
  [Currency.XEU]: 'European Currency Unit',
  [Currency.XFO]: 'Gold franc',
  [Currency.XFU]: 'UIC franc',
  [Currency.XOF]: 'CFA franc BCEAO',
  [Currency.XPD]: 'Palladium (one troy ounce)',
  [Currency.XPF]: 'CFP franc',
  [Currency.XPT]: 'Platinum (one troy ounce)',
  [Currency.YDD]: 'South Yemeni dinar',
  [Currency.YER]: 'Yemeni rial',
  [Currency.YUD]: 'Yugoslav dinar',
  [Currency.YUG]: 'Yugoslav dinar',
  [Currency.YUM]: 'Yugoslav dinar',
  [Currency.YUN]: 'Yugoslav dinar',
  [Currency.YUO]: 'Yugoslav dinar',
  [Currency.YUR]: 'Yugoslav dinar',
  [Currency.ZAC]: 'South African commercial rand',
  [Currency.ZAL]: 'South African financial rand',
  [Currency.ZAR]: 'South African rand',
  [Currency.ZMK]: 'Zambian kwacha',
  [Currency.ZMW]: 'Zambian new kwacha',
  [Currency.ZRN]: 'Zairean new zaire',
  [Currency.ZRZ]: 'Zairean zaire',
  [Currency.ZWC]: 'Rhodesian dollar',
  [Currency.ZWD]: 'Zimbabwe dollar',
  [Currency.ZWL]: 'Zimbabwe dollar',
  [Currency.ZWN]: 'Zimbabwe dollar',
  [Currency.ZWR]: 'Zimbabwe dollar',
  [Currency.ZWX]: 'RTGS dollar',
}
